/**
 * @fileoverview Tracking Retry Strategy
 * @module services/tracking/retry-strategy
 *
 * Implements exponential backoff with jitter for reliable event retries.
 * Prevents hammering the API when backend is temporarily unavailable.
 */

/**
 * Configuration for retry strategy
 */
export interface RetryStrategyConfig {
  /** Base delay in milliseconds (default: 1000) */
  baseDelay: number
  /** Maximum delay in milliseconds (default: 30000) */
  maxDelay: number
  /** Maximum number of retry attempts (default: 5) */
  maxAttempts: number
  /** Enable jitter to prevent thundering herd (default: true) */
  enableJitter: boolean
}

/**
 * Retry state for a specific event batch
 */
export interface RetryState {
  /** Number of attempts made */
  attempts: number
  /** Timestamp when next retry should occur */
  nextRetryTime: number
  /** Last error that occurred */
  lastError: Error | null
}

/**
 * Tracking Retry Strategy
 *
 * Implements exponential backoff with jitter to handle transient failures gracefully.
 * Prevents API hammering when backend is temporarily unavailable.
 *
 * @example
 * ```typescript
 * const strategy = new TrackingRetryStrategy({
 *   baseDelay: 1000,
 *   maxDelay: 30000,
 *   maxAttempts: 5,
 *   enableJitter: true
 * });
 *
 * // Check if should retry
 * if (strategy.shouldRetry(eventKey)) {
 *   // Retry the request
 * }
 *
 * // Record failure
 * strategy.recordFailure(eventKey, error);
 *
 * // Reset on success
 * strategy.recordSuccess(eventKey);
 * ```
 */
export class TrackingRetryStrategy {
  private readonly config: RetryStrategyConfig
  private readonly retryStates = new Map<string, RetryState>()

  /**
   * Create a new retry strategy instance
   * @param config - Configuration options
   */
  constructor(config: Partial<RetryStrategyConfig> = {}) {
    this.config = {
      baseDelay: config.baseDelay ?? 1000,
      maxDelay: config.maxDelay ?? 30000,
      maxAttempts: config.maxAttempts ?? 5,
      enableJitter: config.enableJitter ?? true,
    }

    // Validate configuration
    if (this.config.baseDelay <= 0) {
      throw new Error('baseDelay must be greater than 0')
    }
    if (this.config.maxDelay < this.config.baseDelay) {
      throw new Error('maxDelay must be >= baseDelay')
    }
    if (this.config.maxAttempts <= 0) {
      throw new Error('maxAttempts must be greater than 0')
    }
  }

  /**
   * Check if a request should be retried
   * @param key - Unique identifier for the request batch
   * @returns true if should retry, false if should wait or drop
   */
  shouldRetry(key: string): boolean {
    const state = this.retryStates.get(key)

    // No state = first attempt, always retry
    if (!state) {
      return true
    }

    // Max attempts exceeded, don't retry
    if (state.attempts >= this.config.maxAttempts) {
      return false
    }

    // Check if enough time has passed
    return Date.now() >= state.nextRetryTime
  }

  /**
   * Check if max retries have been exceeded
   * @param key - Unique identifier for the request batch
   * @returns true if max retries exceeded
   */
  isMaxRetriesExceeded(key: string): boolean {
    const state = this.retryStates.get(key)
    return state ? state.attempts >= this.config.maxAttempts : false
  }

  /**
   * Record a failed attempt and calculate next retry time
   * @param key - Unique identifier for the request batch
   * @param error - The error that occurred
   */
  recordFailure(key: string, error: Error): void {
    const state = this.retryStates.get(key) || {
      attempts: 0,
      nextRetryTime: 0,
      lastError: null,
    }

    state.attempts++
    state.lastError = error

    // Calculate next retry time with exponential backoff
    if (state.attempts < this.config.maxAttempts) {
      const delay = this.calculateBackoffDelay(state.attempts)
      state.nextRetryTime = Date.now() + delay
    }

    this.retryStates.set(key, state)
  }

  /**
   * Record a successful attempt and reset retry state
   * @param key - Unique identifier for the request batch
   */
  recordSuccess(key: string): void {
    this.retryStates.delete(key)
  }

  /**
   * Get the current retry state for a key
   * @param key - Unique identifier for the request batch
   * @returns Retry state or undefined if not found
   */
  getState(key: string): RetryState | undefined {
    return this.retryStates.get(key)
  }

  /**
   * Get the number of attempts for a key
   * @param key - Unique identifier for the request batch
   * @returns Number of attempts (0 if not found)
   */
  getAttempts(key: string): number {
    return this.retryStates.get(key)?.attempts ?? 0
  }

  /**
   * Get the time until next retry in milliseconds
   * @param key - Unique identifier for the request batch
   * @returns Milliseconds until next retry (0 if should retry now, -1 if max retries exceeded)
   */
  getTimeUntilNextRetry(key: string): number {
    const state = this.retryStates.get(key)

    if (!state) {
      return 0 // Should retry now
    }

    if (state.attempts >= this.config.maxAttempts) {
      return -1 // Max retries exceeded
    }

    const timeUntil = state.nextRetryTime - Date.now()
    return Math.max(0, timeUntil)
  }

  /**
   * Clear all retry states
   */
  clear(): void {
    this.retryStates.clear()
  }

  /**
   * Calculate exponential backoff delay with optional jitter
   * @param attempt - The attempt number (1-based)
   * @returns Delay in milliseconds
   */
  private calculateBackoffDelay(attempt: number): number {
    // Exponential backoff: baseDelay * 2^(attempt-1)
    const exponentialDelay = Math.min(
      this.config.baseDelay * Math.pow(2, attempt - 1),
      this.config.maxDelay
    )

    // Add jitter to prevent thundering herd
    if (this.config.enableJitter) {
      const jitter = Math.random() * this.config.baseDelay
      return exponentialDelay + jitter
    }

    return exponentialDelay
  }

  /**
   * Get configuration
   * @returns Current configuration
   */
  getConfig(): Readonly<RetryStrategyConfig> {
    return Object.freeze({ ...this.config })
  }

  /**
   * Get all retry states (for debugging/monitoring)
   * @returns Map of all retry states
   */
  getAllStates(): ReadonlyMap<string, RetryState> {
    return new Map(this.retryStates)
  }
}

